import { useEffect, useState } from 'react';
import {
  ANA_PARA_BIRIMI, TOHUM_KUR, kurus, kuruBul, paraBirimiNormalle,
  type KurKaydi, type ParaBirimi,
} from '@nero/shared';
import { api } from './api.js';

/**
 * GÜNLÜK KUR — ARAYÜZ TARAFI
 *
 * ─── İSTEK ───
 * "güncel kur yazmaya gerek yok. sadece mesela 400 euro ödeme aldıysam
 *  euro seçip girebileyim."
 *
 * Kur bu yüzden bir FORM ALANI DEĞİLDİR. Ekranda yalnız bilgi olarak,
 * yazdığın sayının altında küçük bir satır hâlinde görünür:
 *   "≈ 20.000,00 ₺ · 1 € = 50,0000 · 14.09.2026"
 *
 * ─── TEK İSTEK, PAYLAŞILAN DEĞER ───
 * Kasa penceresinde üç, proformada on ayrı para girdisi olabilir. Her biri
 * kendi `useVeri('/kur')` çağrısını yapsaydı aynı kur onlarca kez
 * çekilirdi. Bu yüzden kur MODÜL SEVİYESİNDE bir kez çekilir ve bütün
 * bileşenler aynı değeri dinler.
 */

export interface KurDurumu extends KurKaydi {
  /** Kur gerçekten eski mi — ekranda uyarı çıkarmak için. */
  bayat: boolean;
}

let onbellek: KurDurumu | null = null;
let istek: Promise<KurDurumu | null> | null = null;
const dinleyiciler = new Set<(k: KurDurumu | null) => void>();

/**
 * Kur çekilemezse (ağ hatası, sunucu kapalı) TOHUM kur kullanılır ve
 * kaynağı 'tohum' göründüğü için kullanıcı bunu ekranda görür. `null`
 * dönmek, dövizi tamamen kullanılamaz hâle getirirdi.
 */
function tohumDurumu(): KurDurumu {
  return { ...TOHUM_KUR, bayat: true };
}

async function kuruCek(): Promise<KurDurumu | null> {
  try {
    const k = await api.al<KurDurumu>('/kur');
    onbellek = k;
  } catch {
    /* Oturum açılmadan ya da ağ yokken çağrılabilir; ekranı kilitlemez. */
    onbellek = onbellek ?? tohumDurumu();
  }
  for (const d of dinleyiciler) d(onbellek);
  return onbellek;
}

/** Kuru yeniden çeker — yönetici "şimdi güncelle" dedikten sonra. */
export function kuruTazele(): void {
  istek = kuruCek();
}

export function useKur(): KurDurumu | null {
  const [durum, setDurum] = useState<KurDurumu | null>(onbellek);

  useEffect(() => {
    dinleyiciler.add(setDurum);
    if (!onbellek && !istek) istek = kuruCek();
    else if (onbellek) setDurum(onbellek);
    return () => { dinleyiciler.delete(setDurum); };
  }, []);

  return durum;
}

/**
 * Yazılan sayının TL karşılığı. Kur bilinmiyorsa `null` döner ve ekran
 * hiçbir şey göstermez — uydurma bir karşılık göstermektense sessiz
 * kalmak dürüsttür.
 */
export function tlKarsiligi(
  yazilan: number | string, birim: unknown, kayit: KurKaydi | null,
): number | null {
  const kod = paraBirimiNormalle(birim);
  const sayi = Number(yazilan);
  if (!Number.isFinite(sayi) || sayi === 0) return null;
  if (kod === ANA_PARA_BIRIMI) return null;   /* TL'nin TL karşılığı gösterilmez */
  const kur = kuruBul(kayit, kod);
  return kur === null ? null : kurus(sayi * kur);
}

/** Ekranda gösterilecek kur satırı: "1 € = 50,0000 · 14.09.2026". */
export function kurSatiri(birim: ParaBirimi, kayit: KurKaydi | null): string | null {
  if (birim === ANA_PARA_BIRIMI) return null;
  const kur = kuruBul(kayit, birim);
  if (kur === null || !kayit) return null;
  const kaynak = kayit.kaynak === 'TCMB' ? '' : ` · ${kayit.kaynak === 'elle' ? 'elle girildi' : 'varsayılan kur'}`;
  return `1 ${birim} = ${kur.toLocaleString('tr-TR', { maximumFractionDigits: 4 })}`
    + ` · ${kayit.tarih.split('-').reverse().join('.')}${kaynak}`;
}
